import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from 'react';

export type Placer = {
  held: string | null;
  dragging: string | null;
  landing: string | null;
  hold(id: string | null): void;
  place(chip: string, target: string): void;
  onChipPointerDown(id: string, e: React.PointerEvent<HTMLElement>): void;
  offset: { x: number; y: number };
};

const SNAP = 90;

function targetAt(x: number, y: number): string | null {
  for (const el of document.elementsFromPoint(x, y)) {
    const id = (el as HTMLElement).closest?.('[data-drop]')?.getAttribute('data-drop');
    if (id) return id;
  }
  let best: string | null = null;
  let bestD = SNAP;
  document.querySelectorAll<HTMLElement>('[data-drop]').forEach((el) => {
    if (el.getAttribute('aria-disabled') === 'true') return;
    const r = el.getBoundingClientRect();
    const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
    if (d < bestD) { bestD = d; best = el.getAttribute('data-drop'); }
  });
  return best;
}

const LAND_MS = 200;

export function usePlacer(onPlace: (chip: string, target: string) => void): Placer {
  const [held, setHeld] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [landing, setLanding] = useState<string | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const live = useRef(onPlace);
  live.current = onPlace;

  const place = useCallback((chip: string, target: string) => {
    setHeld(null);
    live.current(chip, target);
  }, []);

  const onChipPointerDown = useCallback((id: string, e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    const start = { x: e.clientX, y: e.clientY };
    let moved = false;
    el.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const dx = ev.clientX - start.x, dy = ev.clientY - start.y;
      if (!moved && Math.hypot(dx, dy) > 8) { moved = true; setDragging(id); setHeld(id); }
      if (moved) setOffset({ x: dx, y: dy });
    };
    const up = (ev: PointerEvent) => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      if (moved) {
        el.style.visibility = 'hidden';
        const t = targetAt(ev.clientX, ev.clientY);
        el.style.visibility = '';
        setDragging(null);
        const spot = t ? document.querySelector<HTMLElement>(`[data-drop="${CSS.escape(t)}"]`) : null;
        const still = document.documentElement.getAttribute('data-reduce-motion') === 'true';
        if (!t || !spot || still) {
          setOffset({ x: 0, y: 0 });
          if (t) place(id, t); else setHeld(null);
          return;
        }
        const c = el.getBoundingClientRect();
        const s = spot.getBoundingClientRect();
        const dx = ev.clientX - start.x, dy = ev.clientY - start.y;
        setOffset({
          x: s.left + s.width / 2 - (c.left + c.width / 2 - dx),
          y: s.top + s.height / 2 - (c.top + c.height / 2 - dy),
        });
        setLanding(id);
        window.setTimeout(() => {
          setLanding(null);
          setOffset({ x: 0, y: 0 });
          place(id, t);
        }, LAND_MS);
      } else {
        setHeld((h) => (h === id ? null : id));
      }
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  }, [place]);

  return { held, dragging, landing, hold: setHeld, place, onChipPointerDown, offset };
}

export function Chip({ placer, id, className, label, children, onTap, disabled }: {
  placer: Placer;
  id: string;
  className?: string;
  label: string;
  children: ReactNode;
  onTap?: () => void;
  disabled?: boolean;
}) {
  const isHeld = placer.held === id;
  const isDragging = placer.dragging === id;
  const isLanding = placer.landing === id;
  const style: CSSProperties = isDragging
    ? { transform: `translate(${placer.offset.x}px, ${placer.offset.y}px) scale(1.06)`, zIndex: 50, transition: 'none' }
    : isLanding
      ? { transform: `translate(${placer.offset.x}px, ${placer.offset.y}px) scale(0.5)`, opacity: 0.6, zIndex: 50, pointerEvents: 'none', transition: `transform ${LAND_MS}ms ease-out, opacity ${LAND_MS}ms ease-out` }
      : {};
  return (
    <button
      key={id}
      type="button"
      className={`g-chip${isHeld ? ' is-held' : ''}${isDragging ? ' is-dragging' : ''}${className ? ` ${className}` : ''}`}
      aria-pressed={isHeld}
      aria-label={label}
      disabled={disabled}
      style={style}
      onPointerDown={(e) => { if (!disabled) { onTap?.(); placer.onChipPointerDown(id, e); } }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap?.(); placer.hold(isHeld ? null : id); }
      }}
    >
      {children}
    </button>
  );
}

export function DropSpot({ placer, id, className, style, label, children, inert }: {
  placer: Placer;
  id: string;
  className?: string;
  style?: CSSProperties;
  label: string;
  children?: ReactNode;
  inert?: boolean;
}) {
  return (
    <button
      type="button"
      data-drop={inert ? undefined : id}
      aria-disabled={inert || !placer.held ? 'true' : 'false'}
      aria-label={label}
      className={`g-spot${placer.held && !inert ? ' is-open' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      onClick={() => { if (placer.held && !inert) placer.place(placer.held, id); }}
    >
      {children}
    </button>
  );
}
